import { useState, useCallback } from "react";
import { ApiError } from "@/lib/api-client";
import { useTenantStore } from "@/stores/tenant-store";
import type {
  BulkImportRecord,
  BulkImportType,
  CreateBulkImportBatchResult,
} from "../types";

interface UseBulkImportUploadResult<
  TNormalized extends BulkImportRecord = BulkImportRecord,
> {
  result: CreateBulkImportBatchResult<TNormalized> | null;
  isUploading: boolean;
  error: Error | null;
  upload: (
    importType: BulkImportType,
    file: File,
  ) => Promise<CreateBulkImportBatchResult<TNormalized>>;
  reset: () => void;
}

export function useBulkImportUpload<
  TNormalized extends BulkImportRecord = BulkImportRecord,
>(): UseBulkImportUploadResult<TNormalized> {
  const [result, setResult] =
    useState<CreateBulkImportBatchResult<TNormalized> | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const upload = useCallback(
    async (
      importType: BulkImportType,
      file: File,
    ): Promise<CreateBulkImportBatchResult<TNormalized>> => {
      setIsUploading(true);
      setError(null);
      setResult(null);

      try {
        const formData = new FormData();
        formData.append("file", file);

        const data = await uploadBulkImportFile<
          CreateBulkImportBatchResult<TNormalized>
        >(`/api/bulk-imports/${importType}/upload`, formData);
        setResult(data);
        return data;
      } catch (err) {
        const nextError = toError(err);
        setError(nextError);
        throw err;
      } finally {
        setIsUploading(false);
      }
    },
    [],
  );

  const reset = useCallback(() => {
    setResult(null);
    setIsUploading(false);
    setError(null);
  }, []);

  return {
    result,
    isUploading,
    error,
    upload,
    reset,
  };
}

async function uploadBulkImportFile<T>(
  endpoint: string,
  body: FormData,
): Promise<T> {
  const tenantId = useTenantStore.getState().activeTenantId;
  const headers: HeadersInit = {
    Accept: "application/json",
  };

  if (tenantId) {
    (headers as Record<string, string>)["x-tenant-id"] = tenantId;
  }

  const response = await fetch(endpoint, {
    method: "POST",
    credentials: "include",
    headers,
    body,
  });

  if (!response.ok) {
    let errorData: unknown;
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: response.statusText };
    }

    const message = getErrorMessage(errorData, response.statusText);
    throw new ApiError(
      message,
      response.status,
      response.statusText,
      errorData,
    );
  }

  return response.json() as Promise<T>;
}

function getErrorMessage(errorData: unknown, fallback: string): string {
  return typeof errorData === "object" &&
    errorData !== null &&
    "message" in errorData &&
    typeof (errorData as { message: unknown }).message === "string"
    ? (errorData as { message: string }).message
    : fallback;
}

function toError(error: unknown): Error {
  if (error instanceof ApiError) {
    return error;
  }

  return error instanceof Error ? error : new Error("Unknown error");
}
